import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const DAY_NAMES_JP = ["月", "火", "水", "木", "金", "土", "日"];

export default function OfficeBusinessDaysManagement() {
  const [days, setDays] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [calendarDays, setCalendarDays] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    generateCalendar();
  }, [currentDate, days]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/office-business-days`, getAuthHeaders());
      setDays(res.data || []);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // 月の最初の日と最後の日
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // カレンダーの開始位置（月曜始まり）
    const startDayOfWeek = firstDay.getDay(); // 0=日曜
    const adjustedStart = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // 月曜=0に調整
    
    const daysInMonth = lastDay.getDate();
    const calendarData = [];
    
    // 前月の日付
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = adjustedStart - 1; i >= 0; i--) {
      calendarData.push({
        date: null,
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
      });
    }
    
    // 当月の日付
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = days.find(d => d.date === dateStr);
      const isOpen = dayData ? dayData.is_open : true; // デフォルトは営業日
      
      calendarData.push({
        date: dateStr,
        day,
        isCurrentMonth: true,
        isOpen,
        note: dayData?.note,
        id: dayData?.id,
      });
    }
    
    // 次月の日付（カレンダーを6週間分埋める）
    const remainingDays = 42 - calendarData.length; // 6週×7日=42
    for (let day = 1; day <= remainingDays; day++) {
      calendarData.push({
        date: null,
        day,
        isCurrentMonth: false,
      });
    }
    
    setCalendarDays(calendarData);
  };

  const toggleDayStatus = async (dayInfo) => {
    if (!dayInfo.isCurrentMonth || !dayInfo.date) return;
    
    try {
      if (dayInfo.id) {
        // 既存の設定を更新
        const newStatus = !dayInfo.isOpen;
        await axios.put(
          `${API}/office-business-days/${dayInfo.id}`,
          { is_open: newStatus },
          getAuthHeaders()
        );
        toast.success(newStatus ? "営業日に設定しました" : "休業日に設定しました");
      } else {
        // 新規作成（休業日として登録）
        await axios.post(
          `${API}/office-business-days`,
          {
            date: dayInfo.date,
            is_open: false,
            note: "",
          },
          getAuthHeaders()
        );
        toast.success("休業日に設定しました");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return <div className="text-center py-8">読み込み中...</div>;
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <CalendarIcon className="h-6 w-6 text-slate-600" />
          事務所営業日管理
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          カレンダーの日付をクリックして営業日/休業日を設定できます（東京時間基準）
        </p>
      </div>

      {/* カレンダーコントロール */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-3">
          <Button onClick={prevMonth} variant="outline" size="sm">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-base font-semibold">
            {currentDate.getFullYear()}年 {currentDate.getMonth() + 1}月
          </div>
          <Button onClick={nextMonth} variant="outline" size="sm">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex justify-center mb-3">
          <Button onClick={goToToday} variant="outline" size="sm">
            今日
          </Button>
        </div>

        {/* カレンダーグリッド */}
        <div className="grid grid-cols-7 gap-1">
          {/* 曜日ヘッダー */}
          {DAY_NAMES_JP.map((name, i) => (
            <div
              key={i}
              className={`text-center text-xs font-semibold py-1 ${
                i === 6 ? "text-red-600" : i === 5 ? "text-blue-600" : "text-slate-600"
              }`}
            >
              {name}
            </div>
          ))}

          {/* 日付セル */}
          {calendarDays.map((dayInfo, idx) => {
            const dayOfWeek = idx % 7;
            const isSunday = dayOfWeek === 6;
            const isSaturday = dayOfWeek === 5;
            const isToday =
              dayInfo.isCurrentMonth &&
              dayInfo.date === new Date().toISOString().split('T')[0];

            return (
              <div
                key={idx}
                onClick={() => toggleDayStatus(dayInfo)}
                className={`
                  aspect-square flex items-center justify-center rounded text-xs cursor-pointer
                  transition-all border
                  ${!dayInfo.isCurrentMonth ? "text-slate-300 bg-slate-50" : ""}
                  ${dayInfo.isCurrentMonth && dayInfo.isOpen ? "bg-white border-slate-200 hover:bg-emerald-50" : ""}
                  ${dayInfo.isCurrentMonth && !dayInfo.isOpen ? "bg-red-100 border-red-300 text-red-700 font-semibold" : ""}
                  ${isToday ? "ring-2 ring-emerald-500" : ""}
                  ${isSunday && dayInfo.isCurrentMonth ? "text-red-600" : ""}
                  ${isSaturday && dayInfo.isCurrentMonth && dayInfo.isOpen ? "text-blue-600" : ""}
                `}
              >
                {dayInfo.day}
              </div>
            );
          })}
        </div>

        {/* 凡例 */}
        <div className="mt-6 flex gap-4 text-xs text-slate-600 justify-center">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-white border border-slate-200 rounded"></div>
            <span>営業日</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
            <span>休業日</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 ring-2 ring-emerald-500 rounded"></div>
            <span>今日</span>
          </div>
        </div>
      </div>

      {/* 説明 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">重要：営業日設定について</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 日付をクリックすると営業日/休業日をトグル切替できます</li>
          <li>• デフォルトは全て営業日です</li>
          <li>• 休業日に設定すると、その前日が発注締切となります</li>
          <li>• <strong>すべての時刻は東京時間（JST）で統一されています</strong></li>
        </ul>
      </div>
    </div>
  );
}
